import React from 'react'
import styled from '@emotion/styled'
import { useOutsideClick } from 'rooks'
import { InfiniteData, useMutation, useQuery } from '@tanstack/react-query'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { Button } from '@mobi/component-library/Base/Buttons'
import { Tooltip } from '@mobi/component-library/Overlays/Tooltip'
import { addToast } from '@mobi/component-library/Feedback/Toast'
import { colors } from '@mobi/component-library/Foundations'
import { queryClient } from '@core/Data/ReactQuery'
import { queryKeys } from '@core/Data/ReactQuery/constants'
import { QuickbetClosed, SetActiveInvestment } from '@core/Areas/Quickbet/signals'
import { type QuickbetState, state$ as quickbetState$ } from '@core/Areas/Quickbet/driver'
import {
  FOB_TICKET_PLACED_BET_TYPE,
  TOTE_TICKET_PLACED_BET_TYPE,
} from '@core/Areas/ShareBet/LoadBet/constants'
import { createTicketLoadBetDetails } from '@core/Areas/ShareBet/LoadBet/createTicketLoadBetDetails'
import { loadBet } from '@core/Areas/ShareBet/LoadBet'
import { LoadBetErrors } from '@core/Areas/ShareBet/LoadBet/constants'
import { isKambiSportBetDetails } from '@core/Areas/ShareBet/LoadBet/typeGuards'
import { createKambiCouponLoadBetDetails } from '@core/Areas/ShareBet/LoadBet/createKambiCouponLoadBetDetails'
import { handleBetSelection } from '@core/Utils/betting/handleBetSelection'
import type { BetLoopSharedBet } from '@core/Areas/BetLoop/types'
import {
  trackBetLoopCopyBet,
  trackBetLoopCopyBetError,
  trackBetLoopViewMyProfile,
  trackBetLoopViewProfile,
} from '@core/Areas/BetLoop/helpers/analytics'
import { useOnboardingTooltips } from '@core/Areas/BetLoop/hooks/useOnboardingTooltips'
import { AppRoutes } from '@core/AppRoutes'
import { appHistory } from '@core/Utils/routing/history'
import { openShareOutgoing } from '@core/Areas/ShareBet/ShareBetOutgoing/helpers/emitter'
import { isKambiSharedCoupon } from '@core/Areas/BetLoop/helpers/typeGuards/isKambiSharedCoupon'
import { betLoopPrincipleProfileQuery } from '@core/Areas/BetLoop/helpers/queries'
import { patchFeedDataShareWithBettingClosed } from '@core/Areas/BetLoop/helpers/patchCachedData'
import { ProfileContext } from '@core/Areas/BetLoop/Components/ProfileDisplay/context'
import { FollowButton } from '@core/Areas/BetLoop/Components/Common/FollowButton'
import { PuntersCopiedLabel } from './Components/PuntersCopiedLabel'
import { BetSelectionDisplay } from './Components/BetSelectionDisplay'
import { KambiBetSelectionDisplay } from './Components/KambiBetSelectionDisplay'
import { CardContainer } from './Components/CardContainer'
import { Header } from './Components/Header'

type Props = {
  sharedBet: BetLoopSharedBet
  shouldAutoOpenQuickbet?: boolean
  isMyCopiedBet?: boolean
}

export const SharedBetCard = React.forwardRef<HTMLDivElement, Props>(
  ({ sharedBet, shouldAutoOpenQuickbet, isMyCopiedBet }, ref) => {
    const { data: myProfile } = useQuery(betLoopPrincipleProfileQuery)

    const { profilePageId, isOnProfilePage, scrollToDefaultProfileState } =
      React.useContext(ProfileContext)

    const quickbetSubscriptionRef = React.useRef<Rx.IDisposable>()
    const copyBetCountButtonRef = React.useRef<HTMLButtonElement>(null)

    const [isCopyCountTooltipVisible, setCopyCountTooltipVisible] = React.useState(false)
    const [shouldDisableCopyBet, setShouldDisableCopyBet] = React.useReducer(() => true, false)

    useOutsideClick(copyBetCountButtonRef, () => setCopyCountTooltipVisible(false))

    React.useEffect(() => {
      return () => quickbetSubscriptionRef.current?.dispose?.()
    }, [])

    const { isPending, mutate } = useMutation({
      mutationKey: ['bet-loop-shared-bet', sharedBet.postId],
      mutationFn: openSharedBetInQuickbet,
      onSuccess() {
        const { isFollowing, ticket, ownerAccountId } = sharedBet
        trackBetLoopCopyBet({ isFollowing, ticket, share_origin: ownerAccountId })
      },
      onError(err) {
        const id = 'copy-bet-error'
        addToast({ id, title: err.message, icon: 'error', theme: 'dark', isDismissable: true })
        const { isFollowing, ticket } = sharedBet
        trackBetLoopCopyBetError({ message: err.message ?? '', isFollowing, ticket })

        if (err.message === LoadBetErrors.BettingClosed) {
          patchFeedDataShareWithBettingClosed(sharedBet.postId)
        }
      },
    })

    const handleCopyBetClick = React.useCallback(() => {
      quickbetSubscriptionRef.current = QuickbetClosed.signal$
        .withLatestFrom(quickbetState$, (_, quickbetState) => ({ quickbetState }))
        .take(1)
        .subscribe(x => {
          const quickbetState: QuickbetState = x.quickbetState.toJS()
          if (
            quickbetState.betPlaced &&
            quickbetState.copiedBetLoopPostId === sharedBet.postId.toString()
          ) {
            queryClient.setQueriesData<InfiniteData<BetLoopSharedBet[]>>(
              { queryKey: queryKeys.betLoopSharedBetsFeedBase() },
              oldData => updateFeedCacheWithIncrementedCopyCount(oldData, sharedBet.postId)
            )
          }
        })
      mutate(sharedBet)
    }, [mutate, sharedBet])

    React.useEffect(() => {
      if (!shouldAutoOpenQuickbet) return
      const timerId = setTimeout(handleCopyBetClick, 1_000)
      return () => clearTimeout(timerId)
    }, [shouldAutoOpenQuickbet, handleCopyBetClick])

    const handleCopyBetCountClick = () => {
      setCopyCountTooltipVisible(curr => !curr)
    }

    const handleProfileClick = () => {
      if (isOnProfilePage && profilePageId === sharedBet.ownerAccountId) {
        scrollToDefaultProfileState?.()
      } else if (myProfile?.id === sharedBet.ownerAccountId) {
        trackBetLoopViewMyProfile()
        appHistory.push(AppRoutes.BetLoopMyProfile)
      } else {
        trackBetLoopViewProfile()
        appHistory.push(`${AppRoutes.BetLoop}/profile/${sharedBet.ownerAccountId}`)
      }
    }

    const isMySharedBet = myProfile?.id === sharedBet.ownerAccountId
    const shouldDisplayCopyBetButton =
      !isMySharedBet && !isMyCopiedBet && !shouldDisableCopyBet && canBetBeCopied(sharedBet)

    const viewUserProfileTooltipRef = React.useRef<HTMLDivElement>(null)
    const { currentTooltipId, onDismissed } = useOnboardingTooltips('bet-loop-view-user-profile')
    const shouldShowTooltip = currentTooltipId === 'bet-loop-view-user-profile'
    useOutsideClick(viewUserProfileTooltipRef, () => {
      if (shouldShowTooltip) onDismissed('bet-loop-view-user-profile')
    })

    return (
      <CardContainer
        ref={ref}
        headerSection={
          <>
            <Header
              sharedBet={sharedBet}
              handleProfileClick={handleProfileClick}
              isOnProfilePage={isOnProfilePage}
              avatarProfileRef={viewUserProfileTooltipRef}
              rightAlignedButton={
                isMySharedBet ? (
                  <Button
                    size='sm'
                    isIconOnlyButton
                    color='secondary_color'
                    onClick={() =>
                      openShareOutgoing({
                        contextType: 'betloop',
                        betSource: 'share-bet',
                        share: sharedBet,
                      })
                    }
                  >
                    <Icon name='LineShare02' size='2rem' />
                  </Button>
                ) : (
                  <FollowButton
                    accountId={sharedBet.ownerAccountId}
                    accountDisplayName={sharedBet.ownerAccountDisplayName}
                    isFollowing={sharedBet.isFollowing}
                    isNotificationsEnabled={sharedBet.isNotificationsEnabled}
                    location='shared-bet-card'
                    isOnProfilePage={isOnProfilePage}
                  />
                )
              }
            />

            {shouldShowTooltip && (
              <Tooltip
                color='blue'
                hasTooltipBeenSeen={() => false}
                shouldHideCrossIcon
                triggerElRef={viewUserProfileTooltipRef}
                tooltipId='bet-loop-view-user-profile'
                offsetYPx={8}
                offsetXPx={40}
                flipElement={true}
                shouldOnlyRenderSingleInstance={true}
                onDismissed={() => onDismissed('bet-loop-view-user-profile')}
              >
                <div style={{ minWidth: '11.2rem' }}>Tap to view stats</div>
                <div>and shared bets</div>
              </Tooltip>
            )}
          </>
        }
      >
        {isKambiSharedCoupon(sharedBet.ticket) ? (
          <KambiBetSelectionDisplay
            coupon={sharedBet.ticket}
            handleDisableCopyBet={setShouldDisableCopyBet}
          />
        ) : (
          <BetSelectionDisplay
            onPendingStatusChange={setShouldDisableCopyBet}
            ticket={sharedBet.ticket}
            isResulted={sharedBet.isResulted}
            postId={sharedBet.postId}
          />
        )}

        {shouldDisplayCopyBetButton ? (
          <CopyBetButtonWrapperStyled>
            <Button
              size='sm'
              isFullWidth
              color='positive'
              isLoading={isPending}
              onClick={handleCopyBetClick}
            >
              Copy Bet
            </Button>

            <Button
              size='sm'
              isFullWidth
              color='positive'
              ref={copyBetCountButtonRef}
              onClick={handleCopyBetCountClick}
            >
              <Icon name='DuocolorUsers02' color='white' size='1.6rem' />
              <span> {sharedBet.copyCount}</span>
            </Button>

            {isCopyCountTooltipVisible && (
              <Tooltip
                color='white'
                shouldHideCrossIcon
                hasTooltipBeenSeen={() => false}
                triggerElRef={copyBetCountButtonRef}
                isHidden={!isCopyCountTooltipVisible}
                tooltipId='bet-loop-copy-bet-count-button'
              >
                <div style={{ textAlign: 'center' }}>
                  Punters who <br /> copied this bet
                </div>
              </Tooltip>
            )}
          </CopyBetButtonWrapperStyled>
        ) : (
          <PuntersCopiedLabel
            copyCount={sharedBet.copyCount}
            shouldUseYourText={!isMyCopiedBet && isMySharedBet}
          />
        )}
      </CardContainer>
    )
  }
)

SharedBetCard.displayName = 'SharedBetCard'

// =============
// Local Helpers
// =============

async function openSharedBetInQuickbet(sharedBet: BetLoopSharedBet) {
  const ticket = sharedBet.ticket
  const isKambiSportsBet = isKambiSharedCoupon(ticket)

  const loadDetails = isKambiSportsBet
    ? createKambiCouponLoadBetDetails(ticket)
    : createTicketLoadBetDetails(ticket)

  const { selections } = await loadBet(loadDetails, {
    location: 'bet-loop',
    origin: sharedBet.ownerAccountId.toString(),
    copiedBetLoopPostId: sharedBet.postId.toString(),
  })

  const investmentType =
    selections.length === 1 &&
    !isKambiSportBetDetails(loadDetails[0]) &&
    [FOB_TICKET_PLACED_BET_TYPE, TOTE_TICKET_PLACED_BET_TYPE].includes(loadDetails[0].betType ?? '')
      ? 'place'
      : 'win'

  const setUp = isKambiSportsBet ? undefined : () => SetActiveInvestment(investmentType)

  handleBetSelection({ betFlow: { location: 'Quickbet', options: { setUp } }, selections })
}

function updateFeedCacheWithIncrementedCopyCount(
  oldData: InfiniteData<BetLoopSharedBet[]> | undefined,
  id: number
): InfiniteData<BetLoopSharedBet[]> | undefined {
  if (!oldData) return oldData
  return {
    ...oldData,
    pages: oldData.pages.map(page =>
      page.some(bet => bet.postId === id)
        ? page.map(bet => (bet.postId === id ? { ...bet, copyCount: bet.copyCount + 1 } : bet))
        : page
    ),
  }
}

function canBetBeCopied(sharedBet: BetLoopSharedBet) {
  if (sharedBet.isResulted || !sharedBet.canCopyBet) return false

  if (isKambiSharedCoupon(sharedBet.ticket)) {
    return sharedBet.ticket.outcomes.every(outcome => outcome.status === 'OPEN')
  }

  return sharedBet.ticket.status === 'Pending'
}

// ======
// Styles
// ======

const CopyBetButtonWrapperStyled = styled.div({
  display: 'flex',
  alignItems: 'stretch',
  gap: 0,

  'button:active': { transform: 'scale(1)' },

  '> button:first-of-type': {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRight: `1px solid ${colors.success[600]}`,
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },

  '> button:last-of-type': {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '0.2rem',
    flexBasis: '8rem',
    borderLeft: `1px solid ${colors.celery[500]}`,
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
  },
})
